import { useState, useEffect } from 'react';
import { api } from './api';

/**
 * The red zone threshold and the CScore weights are needed by almost every
 * list page — the threshold to flag the watch list, the weights to label the
 * hover breakdown. Fetching them per page would fire the same request four or
 * five times on a single navigation, so the in-flight promise is shared
 * process-wide and the resolved value is reused for the rest of the session.
 */
let pending = null;
let cached = null;

const EMPTY = { threshold: null, weights: {} };

function loadScoring() {
  if (cached != null) return Promise.resolve(cached);
  if (!pending) {
    pending = api.settings.scoring()
      .then(({ red_zone_threshold, weights }) => {
        cached = { threshold: red_zone_threshold, weights: weights || {} };
        return cached;
      })
      .catch(err => {
        // A failed read must not flag every clinician on the page, so fall
        // back to no threshold (nobody flagged) and let the next mount retry.
        console.error('Could not load scoring settings:', err);
        pending = null;
        return EMPTY;
      });
  }
  return pending;
}

/** Drop the cache so the next read re-fetches (used after an admin edit). */
export function invalidateScoring() {
  cached = null;
  pending = null;
}

/**
 * Returns { threshold, weights }. `threshold` is null while loading or on
 * failure, which callers treat as "flag nobody".
 */
export function useScoring() {
  const [scoring, setScoring] = useState(cached || EMPTY);

  useEffect(() => {
    let active = true;
    loadScoring().then(value => {
      if (active) setScoring(value);
    });
    return () => { active = false; };
  }, []);

  return scoring;
}

// Lives server-side so the monthly summary email flags the same people.
export { isRedZone } from '../../server/lib/red-zone';
